import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { isPreviewEnvironment } from "../lib/previewEnv";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#F59E0B";

const Shell = ({ children }) => (
  <div className="min-h-screen text-white flex items-center justify-center px-5" style={{ backgroundColor: "#0B1A2E" }} data-testid="member-gate-locked">
    <div className="max-w-md w-full text-center rounded-3xl bg-white/[0.05] ring-1 ring-white/10 p-8 sm:p-10">
      {children}
    </div>
  </div>
);

export const MemberGate = ({ children, paid = false, product = "" }) => {
  const [state, setState] = useState("loading"); // loading | ok | locked | paid-locked | product-locked
  const location = useLocation();

  useEffect(() => {
    const check = () => axios.get(`${API}/members/me`, { withCredentials: true }).then((r) => {
      if (paid && !r.data?.is_paid) setState("paid-locked");
      else if (product && !r.data?.is_admin && !(r.data?.paid_products || []).includes(product)) setState("product-locked");
      else setState("ok");
    });
    // PREVIEW μόνο: αυτόματη σύνδεση με το δοκιμαστικό μέλος, ώστε ο admin να βλέπει τις σελίδες μελών χωρίς login
    const previewAutoLogin = () => axios.post(`${API}/members/login`, { email: "dokimi@peirateia.gr", password: "Peirateia2026!" }, { withCredentials: true }).then(check);
    check().catch(() => (isPreviewEnvironment() ? previewAutoLogin() : Promise.reject()).catch(() => setState("locked")));
  }, [paid, product]);

  if (state === "ok") return children;

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" style={{ backgroundColor: "#0B1A2E" }} data-testid="member-gate-loading">
        Φόρτωση…
      </div>
    );
  }

  if (state === "product-locked") {
    return (
      <Shell>
        <div className="text-5xl mb-4">🔒</div>
        <div className="mb-3">
          <span data-testid="member-gate-product-badge"
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: "rgba(56,189,248,0.12)", color: "#7DD3FC", border: "1px solid rgba(56,189,248,0.45)" }}>
            🔒 Επίπεδο Β
          </span>
        </div>
        <p className="text-[11px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: GOLD }}>Ανοίγει προσωπικά</p>
        <h1 className="text-2xl sm:text-3xl font-bold mb-3" data-testid="member-gate-product-title">
          Αυτό το εργαλείο είναι για όσους περνούν στο Επίπεδο Β
        </h1>
        <p className="text-slate-300 text-sm leading-relaxed mb-6">
          Το ανοίγει ο Κωνσταντίνος προσωπικά, όταν έρθει η στιγμή σου — θα λάβεις email με τον σύνδεσμο.
          Αν πιστεύεις ότι έπρεπε ήδη να είναι ανοιχτό, στείλε του ένα μήνυμα.
        </p>
        <a href="/koinotita" data-testid="member-gate-product-cta"
          className="inline-block px-8 py-3 rounded-full font-semibold text-sm"
          style={{ background: GOLD, color: "#0B1A2E" }}>
          🧰 Τα εργαλεία μου
        </a>
        <p className="text-slate-500 text-xs mt-4 mb-2">Σου το άνοιξε σε άλλο email;</p>
        <button data-testid="member-gate-product-switch"
          onClick={async () => {
            try { await axios.post(`${API}/members/logout`, {}, { withCredentials: true }); } catch (e) { /* noop */ }
            window.location.href = `/koinotita?next=${encodeURIComponent(location.pathname)}`;
          }}
          className="text-sm font-semibold underline underline-offset-4" style={{ color: GOLD }}>
          Σύνδεση με άλλο λογαριασμό →
        </button>
      </Shell>
    );
  }

  if (state === "paid-locked") {
    return (
      <Shell>
        <div className="text-5xl mb-4">🗝️</div>
        <div className="mb-3">
          <span data-testid="member-gate-paid-badge"
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: "rgba(245,158,11,0.15)", color: GOLD, border: "1px solid rgba(245,158,11,0.45)" }}>
            🗝️ 8 Συναντήσεις
          </span>
        </div>
        <p className="text-[11px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: GOLD }}>Χρειάζεται τα θεμέλιά του</p>
        <h1 className="text-2xl sm:text-3xl font-bold mb-3" data-testid="member-gate-paid-title">
          Αυτό το εργαλείο δουλεύει μόνο με την εκπαίδευση
        </h1>
        <p className="text-slate-300 text-sm leading-relaxed mb-8">
          Για να σου δώσει πραγματική αξία, χρειάζεται όσα χτίζονται βήμα-βήμα στην εκπαίδευση —
          αλλιώς θα ήταν απλώς άλλη μια σελίδα που διαβάζεις και ξεχνάς.
          Γι' αυτό ξεκλειδώνει μαζί με τις <strong className="text-white">8 Συναντήσεις: Εσωτερική Ηγεσία</strong> —
          εκεί παίρνεις τη βάση που το κάνει να λειτουργεί.
        </p>
        <a href="/8vimata" data-testid="member-gate-paid-cta"
          className="inline-block px-8 py-3 rounded-full font-semibold text-sm"
          style={{ background: GOLD, color: "#0B1A2E" }}>
          ⛵ Δες τις 8 Συναντήσεις
        </a>
        <p className="text-slate-500 text-xs mt-4 mb-2">
          Έχεις ήδη αγοράσει πρόγραμμα; Συνδέσου με τον λογαριασμό της αγοράς σου:
        </p>
        <button data-testid="member-gate-switch-account"
          onClick={async () => {
            try { await axios.post(`${API}/members/logout`, {}, { withCredentials: true }); } catch (e) { /* noop */ }
            const nx = encodeURIComponent(location.pathname + location.search.replace(/[?&]from=[^&]*/g, ""));
            window.location.href = `/koinotita?next=${nx}`;
          }}
          className="text-sm font-semibold underline underline-offset-4"
          style={{ color: GOLD }}>
          Σύνδεση με άλλο λογαριασμό →
        </button>
      </Shell>
    );
  }

  const next = encodeURIComponent(location.pathname + location.search.replace(/[?&]from=[^&]*/g, ""));
  return (
    <Shell>
      <div className="text-5xl mb-4">🔐</div>
      <p className="text-[11px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: GOLD }}>Μόνο για το πλήρωμα</p>
      <h1 className="text-2xl sm:text-3xl font-bold mb-3" data-testid="member-gate-title">
        Για να ανοίξει, χρειάζεται δωρεάν λογαριασμό
      </h1>
      <p className="text-slate-300 text-sm leading-relaxed mb-2">
        Παίρνει <strong className="text-white">20 δευτερόλεπτα</strong> — με email ή με Google — και
        κρατά τα δικά σου: τις μέρες εξάσκησης, τα ημερολόγια, την πρόοδό σου.
      </p>
      <p className="text-slate-400 text-xs leading-relaxed mb-8">
        Μαζί του παίρνεις όλα τα εργαλεία: το Τεστ, τους Πειρατές σε βάθος, την Άμεση Συνοχή,
        τον Προσομοιωτή, τα Ημερολόγια και τη Βιντεοθήκη.
      </p>
      <a href={`/koinotita?next=${next}`} data-testid="member-gate-cta"
        className="inline-block px-8 py-3 rounded-full font-semibold text-sm"
        style={{ background: GOLD, color: "#0B1A2E" }}>
        🔑 Μπες / Γράψου δωρεάν
      </a>
    </Shell>
  );
};
